import React, { useState, useEffect, useRef } from 'react';
import { StopCircle } from 'lucide-react';

// 30 bars with fixed pseudo-random heights for a stable waveform shape
const BAR_COUNT = 30;
const BAR_HEIGHTS = Array.from({ length: BAR_COUNT }, (_, i) => {
  const v = ((i * 37 + 7) * 13 + i * 5) % 60;
  return 25 + v; // range 25–85 %
});

interface RecordingWaveformProps {
  /** Called when the user presses the stop button */
  onStop: () => void;
  /** Additional Tailwind classes for the outer wrapper */
  className?: string;
}

/**
 * WhatsApp-style animated waveform shown while recording audio.
 * Bars bounce with staggered CSS animations; elapsed time counts up.
 */
const RecordingWaveform: React.FC<RecordingWaveformProps> = ({ onStop, className = '' }) => {
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`flex items-center gap-2 w-full ${className}`}>
      {/* Animated red dot */}
      <span className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />

      {/* Waveform bars */}
      <div className="flex-1 flex items-end gap-[2px]" style={{ height: '32px' }}>
        {BAR_HEIGHTS.map((h, i) => (
          <div
            key={i}
            className="rounded-full bg-red-400"
            style={{
              flex: '1 1 0',
              minWidth: '2px',
              height: `${h}%`,
              animation: `recordingBar 0.8s ease-in-out infinite alternate`,
              animationDelay: `${(i * 40) % 800}ms`,
            }}
          />
        ))}
      </div>

      {/* Elapsed timer */}
      <span className="flex-shrink-0 text-xs font-mono text-red-500 w-8 text-right">
        {formatTime(elapsed)}
      </span>

      {/* Stop button */}
      <button
        onClick={onStop}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors shadow"
        title="Stop recording"
      >
        <StopCircle className="w-4 h-4 text-white fill-white" />
      </button>

      {/* Keyframes injected inline once */}
      <style>{`
        @keyframes recordingBar {
          from { transform: scaleY(0.35); }
          to   { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
};

export default RecordingWaveform;
